import { useState, useEffect, useMemo, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../../lib/supabaseClient';
import { FilterPickerModal, EstimatorPickerModal, StatusPickerModal, COMMENT_MAXLEN } from './RequirementPickers';
import './RequirementForm.css';

/* ── Icons ──────────────────────────────────────── */
function IconArrowLeft() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <line x1="19" y1="12" x2="5" y2="12" />
      <polyline points="12 19 5 12 12 5" />
    </svg>
  );
}

function IconSave() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function IconWarning() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" />
      <line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  );
}

function IconSpinner() {
  return (
    <svg className="rf-spinner-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" aria-hidden="true">
      <path d="M12 2a10 10 0 0 1 10 10" />
    </svg>
  );
}

function IconChevronDown() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="6 9 12 15 18 9" />
    </svg>
  );
}

function IconSuccess() {
  return (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#00CFC1" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
      <polyline points="22 4 12 14.01 9 11.01" />
    </svg>
  );
}

/* ── Contrast helper ── */
function getContrastColor(hex) {
  if (!hex) return '#222222';
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.55 ? '#222222' : '#FFFFFF';
}

/* ── Picker trigger — a button styled like a select that opens a modal ── */
function PickerTrigger({ id, onClick, disabled, placeholder, isEmpty, children }) {
  return (
    <button
      type="button"
      id={id}
      className={`rf-picker-trigger${isEmpty ? ' rf-picker-trigger--empty' : ''}`}
      onClick={onClick}
      disabled={disabled}
    >
      <span className="rf-picker-trigger-label">{isEmpty ? placeholder : children}</span>
      <IconChevronDown />
    </button>
  );
}

/* ── Initial form state ── */
const EMPTY_FORM = {
  client_id: '',
  project_id: '',
  reference_id: '',
  description: '',
  estimator_user_id: '',
  leader_user_id: '',
  status_id: '',
  comment: '',
};

/* ── Component ─────────────────────────────────── */
function RequirementForm({ onBack, onCreated }) {
  /* form data */
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [saving, setSaving] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);

  /* toggle: create multiple */
  const [multiMode, setMultiMode] = useState(false);

  /* cancel confirmation modal */
  const [showCancelModal, setShowCancelModal] = useState(false);

  /* which picker modal is open: 'client'|'project'|'estimator'|'leader'|'status'|null */
  const [picker, setPicker] = useState(null);

  /* catalog data */
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [leaders, setLeaders] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [catalogLoading, setCatalogLoading] = useState(true);

  /* ── Fetch catalogs ── */
  useEffect(() => {
    async function fetchCatalogs() {
      setCatalogLoading(true);
      const [clientsRes, projectsRes, usersRes, statusRes] = await Promise.all([
        supabase.from('clients').select('id, name').eq('active', true).order('name'),
        supabase.from('projects').select('id, name, client_id').eq('active', true).order('name'),
        supabase.from('users').select('id, full_name, roles(name)').eq('active', true).order('full_name'),
        supabase.from('status').select('id, name, color').order('name'),
      ]);
      setClients(clientsRes.data || []);
      setProjects(projectsRes.data || []);
      setAllUsers(usersRes.data || []);
      setLeaders(
        (usersRes.data || []).filter((u) => u.roles?.name === 'Líder' || u.roles?.name === 'Gerente')
      );
      setStatuses(statusRes.data || []);
      setCatalogLoading(false);
    }
    fetchCatalogs();
  }, []);

  /* ── Filtered projects by selected client ── */
  const filteredProjects = useMemo(() => {
    if (!form.client_id) return [];
    return projects.filter((p) => String(p.client_id) === String(form.client_id));
  }, [form.client_id, projects]);

  /* ── When client changes, clear project ── */
  const handleClientChange = useCallback((clientId) => {
    setForm((prev) => ({ ...prev, client_id: clientId, project_id: '' }));
    setErrors((prev) => ({ ...prev, client_id: '', project_id: '' }));
  }, []);

  /* ── Generic field change ── */
  function handleChange(field, value) {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: '' }));
    if (submitError) setSubmitError('');
  }

  /* ── Validation ── */
  function validate() {
    const errs = {};
    if (!form.client_id) errs.client_id = 'Selecciona un cliente';
    if (!form.project_id) errs.project_id = 'Selecciona un proyecto';
    if (!form.reference_id.trim()) errs.reference_id = 'Ingresa el ID Folio';
    if (!form.description.trim()) errs.description = 'Ingresa una descripción';
    if (!form.leader_user_id) errs.leader_user_id = 'Selecciona un líder';
    if (!form.status_id) errs.status_id = 'Selecciona un estatus';
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  /* ── Submit ── */
  async function handleSubmit(e) {
    e.preventDefault();
    if (!validate()) return;

    setSaving(true);
    setSubmitError('');

    const payload = {
      project_id: Number(form.project_id),
      reference_id: form.reference_id.trim(),
      description: form.description.trim(),
      leader_user_id: form.leader_user_id,
      status_id: Number(form.status_id),
      estimator_user_id: form.estimator_user_id || null,
      comment: form.comment.trim() || null,
    };

    const { data: inserted, error } = await supabase
      .from('requirements')
      .insert([payload])
      .select('id')
      .single();

    setSaving(false);

    if (error) {
      if (error.code === '23505' && error.message?.includes('reference_id')) {
        setSubmitError('El ID Folio ya existe. Ingresa uno diferente.');
        setErrors((prev) => ({ ...prev, reference_id: 'Folio duplicado' }));
      } else {
        setSubmitError(error.message || 'Error al crear el requerimiento.');
      }
      return;
    }

    /* fire-and-forget: create SharePoint folder + send notification via edge function */
    if (inserted?.id) {
      supabase.functions.invoke('create-requirement-folder', {
        body: {
          reference_id: payload.reference_id,
          requirement_id: inserted.id,
          estimator_user_id: payload.estimator_user_id || null,
          leader_user_id: payload.leader_user_id,
        },
      }).catch((err) => console.error('Requirement post-creation flows failed:', err));
    }

    /* success */
    if (multiMode) {
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 2500);
      setForm({ ...EMPTY_FORM });
      setErrors({});
    } else {
      onCreated?.();
      onBack();
    }
  }

  /* ── Cancel flow ── */
  function handleCancelClick() {
    const hasData = Object.entries(form).some(
      ([, v]) => typeof v === 'string' && v.trim() !== ''
    );
    if (hasData) {
      setShowCancelModal(true);
    } else {
      onBack();
    }
  }

  function confirmCancel() {
    setShowCancelModal(false);
    onBack();
  }

  /* ── Selected labels for picker triggers ── */
  const selectedClient = clients.find((c) => String(c.id) === String(form.client_id));
  const selectedProject = filteredProjects.find((p) => String(p.id) === String(form.project_id));
  const selectedEstimator = allUsers.find((u) => String(u.id) === String(form.estimator_user_id));
  const selectedLeader = leaders.find((u) => String(u.id) === String(form.leader_user_id));
  const selectedStatus = statuses.find((s) => String(s.id) === String(form.status_id));

  /* ── Skeleton for loading ── */
  if (catalogLoading) {
    return (
      <div className="rf-container">
        <div className="rf-header">
          <div className="rf-skeleton rf-skeleton--title" />
        </div>
        <div className="rf-form-skeleton">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="rf-skeleton rf-skeleton--field" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="rf-container">
      {/* ── Header ── */}
      <div className="rf-header">
        <button className="rf-back-btn" onClick={handleCancelClick} type="button">
          <IconArrowLeft />
          <span>Regresar</span>
        </button>
        <h1 className="rf-title">Creación de Requerimiento</h1>
      </div>

      {/* ── Multi-mode toggle ── */}
      <div className="rf-toggle-row">
        <label className="rf-toggle-label" htmlFor="rf-multi-toggle">
          ¿Vas a subir más de un requerimiento?
        </label>
        <button
          id="rf-multi-toggle"
          type="button"
          role="switch"
          aria-checked={multiMode}
          className={`rf-toggle${multiMode ? ' rf-toggle--on' : ''}`}
          onClick={() => setMultiMode((v) => !v)}
        >
          <span className="rf-toggle-knob" />
        </button>
        <span className="rf-toggle-state">{multiMode ? 'Sí' : 'No'}</span>
      </div>

      {/* ── Success toast (multi-mode) ── */}
      {showSuccess && (
        <div className="rf-toast">
          <IconSuccess />
          <span>Requerimiento creado exitosamente. Puedes crear otro.</span>
        </div>
      )}

      {/* ── Form ── */}
      <form className="rf-form" onSubmit={handleSubmit} noValidate>
        {/* Cliente */}
        <div className={`rf-field${errors.client_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-client">
            Cliente <span className="rf-required">*</span>
          </label>
          <PickerTrigger id="rf-client" onClick={() => setPicker('client')} isEmpty={!selectedClient} placeholder="— Seleccionar cliente —">
            {selectedClient?.name}
          </PickerTrigger>
          {errors.client_id && <span className="rf-error-msg">{errors.client_id}</span>}
        </div>

        {/* Proyecto */}
        <div className={`rf-field${errors.project_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-project">
            Proyecto <span className="rf-required">*</span>
          </label>
          <PickerTrigger
            id="rf-project"
            onClick={() => setPicker('project')}
            disabled={!form.client_id}
            isEmpty={!selectedProject}
            placeholder={form.client_id ? '— Seleccionar proyecto —' : '— Selecciona un cliente primero —'}
          >
            {selectedProject?.name}
          </PickerTrigger>
          {errors.project_id && <span className="rf-error-msg">{errors.project_id}</span>}
        </div>

        {/* ID Folio */}
        <div className={`rf-field${errors.reference_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-folio">
            ID Folio <span className="rf-required">*</span>
          </label>
          <input
            id="rf-folio"
            type="text"
            className="rf-input"
            placeholder="Ej. WSP-REQ-001"
            value={form.reference_id}
            onChange={(e) => handleChange('reference_id', e.target.value)}
          />
          {errors.reference_id && <span className="rf-error-msg">{errors.reference_id}</span>}
        </div>

        {/* Descripción */}
        <div className={`rf-field rf-field--full${errors.description ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-desc">
            Descripción <span className="rf-required">*</span>
          </label>
          <textarea
            id="rf-desc"
            className="rf-textarea"
            rows="3"
            placeholder="Describe el requerimiento…"
            value={form.description}
            onChange={(e) => handleChange('description', e.target.value)}
          />
          {errors.description && <span className="rf-error-msg">{errors.description}</span>}
        </div>

        {/* Estimador */}
        <div className="rf-field">
          <label className="rf-label" htmlFor="rf-estimator">
            Estimador <span className="rf-optional">(opcional)</span>
          </label>
          <PickerTrigger id="rf-estimator" onClick={() => setPicker('estimator')} isEmpty={!selectedEstimator} placeholder="— Sin estimador —">
            {selectedEstimator?.full_name}
          </PickerTrigger>
        </div>

        {/* Líder */}
        <div className={`rf-field${errors.leader_user_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-leader">
            Líder <span className="rf-required">*</span>
          </label>
          <PickerTrigger id="rf-leader" onClick={() => setPicker('leader')} isEmpty={!selectedLeader} placeholder="— Seleccionar líder —">
            {selectedLeader?.full_name}
          </PickerTrigger>
          {errors.leader_user_id && <span className="rf-error-msg">{errors.leader_user_id}</span>}
        </div>

        {/* Estatus */}
        <div className={`rf-field${errors.status_id ? ' rf-field--error' : ''}`}>
          <label className="rf-label" htmlFor="rf-status">
            Estatus <span className="rf-required">*</span>
          </label>
          <PickerTrigger id="rf-status" onClick={() => setPicker('status')} isEmpty={!selectedStatus} placeholder="— Seleccionar estatus —">
            {selectedStatus && (
              <span className="rf-status-preview" style={{ backgroundColor: selectedStatus.color, color: getContrastColor(selectedStatus.color) }}>
                {selectedStatus.name}
              </span>
            )}
          </PickerTrigger>
          {errors.status_id && <span className="rf-error-msg">{errors.status_id}</span>}
        </div>

        {/* Comentarios */}
        <div className="rf-field rf-field--full">
          <label className="rf-label" htmlFor="rf-comments">
            Comentarios <span className="rf-optional">(opcional)</span>
          </label>
          <textarea
            id="rf-comments"
            className="rf-textarea"
            rows="3"
            maxLength={COMMENT_MAXLEN}
            placeholder="Comentarios adicionales…"
            value={form.comment}
            onChange={(e) => handleChange('comment', e.target.value)}
          />
          <span className="rf-char-count">{form.comment.length}/{COMMENT_MAXLEN}</span>
        </div>

        {/* Submit error */}
        {submitError && (
          <div className="rf-submit-error rf-field--full">
            <IconWarning />
            <span>{submitError}</span>
          </div>
        )}

        {/* Actions */}
        <div className="rf-actions rf-field--full">
          <button
            type="button"
            className="rf-btn rf-btn--cancel"
            onClick={handleCancelClick}
            disabled={saving}
          >
            Cancelar
          </button>
          <button
            type="submit"
            className="rf-btn rf-btn--submit"
            disabled={saving}
          >
            {saving ? (
              <>
                <IconSpinner />
                <span>Guardando…</span>
              </>
            ) : (
              <>
                <IconSave />
                <span>Crear Requerimiento</span>
              </>
            )}
          </button>
        </div>
      </form>

      {/* ── Cancel confirmation modal — portaled to document.body so its
           position:fixed resolves to the viewport, not the transformed .req-main. ── */}
      {showCancelModal && createPortal(
        <div className="rf-modal-backdrop" onClick={() => setShowCancelModal(false)}>
          <div className="rf-modal" onClick={(e) => e.stopPropagation()}>
            <div className="rf-modal-icon">
              <IconWarning />
            </div>
            <p className="rf-modal-text">
              Se perderán los datos del registro, ¿estás seguro de querer cancelar la solicitud?
            </p>
            <div className="rf-modal-actions">
              <button
                className="rf-btn rf-btn--modal-back"
                onClick={() => setShowCancelModal(false)}
                type="button"
              >
                No, quiero regresar
              </button>
              <button
                className="rf-btn rf-btn--modal-confirm"
                onClick={confirmCancel}
                type="button"
              >
                Sí, quiero cancelar
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* ── Picker modals — portaled to document.body so position:fixed
           resolves to the viewport, not the transformed .req-main ── */}
      {createPortal(
        <>
          <FilterPickerModal
            open={picker === 'client'}
            title="Seleccionar Cliente"
            options={clients}
            value={form.client_id}
            onSelect={(id) => handleClientChange(id ? String(id) : '')}
            onClose={() => setPicker(null)}
            labelKey="name"
            emptyOption={null}
          />
          <FilterPickerModal
            open={picker === 'project'}
            title="Seleccionar Proyecto"
            options={filteredProjects}
            value={form.project_id}
            onSelect={(id) => handleChange('project_id', id ? String(id) : '')}
            onClose={() => setPicker(null)}
            labelKey="name"
            emptyOption={null}
          />
          <EstimatorPickerModal
            open={picker === 'estimator'}
            options={allUsers}
            value={form.estimator_user_id}
            onSelect={(id) => handleChange('estimator_user_id', id || '')}
            onClose={() => setPicker(null)}
          />
          <EstimatorPickerModal
            title="Seleccionar Líder"
            emptyLabel="Sin líder"
            open={picker === 'leader'}
            options={leaders}
            value={form.leader_user_id}
            onSelect={(id) => handleChange('leader_user_id', id || '')}
            onClose={() => setPicker(null)}
          />
          <StatusPickerModal
            open={picker === 'status'}
            options={statuses}
            value={form.status_id}
            onSelect={(id) => { handleChange('status_id', id ? String(id) : ''); setPicker(null); }}
            onClose={() => setPicker(null)}
          />
        </>,
        document.body
      )}
    </div>
  );
}

export default RequirementForm;
